import { useState, useEffect } from 'react';
import { batchesApi } from '../services/api';

interface BatchItem {
  id: string;
  batchCode: string;
  productName: string | null;
  status: string;
  startedAt: string;
  endedAt: string | null;
  stats?: {
    totalInspections: number;
    passCount: number;
    failCount: number;
    warningCount: number;
    passRate: number;
  };
}

export function Batches() {
  const [batches, setBatches] = useState<BatchItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedBatch, setSelectedBatch] = useState<BatchItem | null>(null);

  useEffect(() => {
    batchesApi.list(50)
      .then((data: any) => setBatches(Array.isArray(data) ? data : []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="loading-page"><div className="spinner" /></div>;

  return (
    <>
      <div className="page-header">
        <h1>Batches</h1>
        <p>Production batch traceability and quality audit trail</p>
      </div>

      <div className="page-body">
        {batches.length === 0 ? (
          <div className="empty-state">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" />
            </svg>
            <h3>No Batches Yet</h3>
            <p>Batches are created automatically when edge devices report inspection data with a batch code.</p>
          </div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Batch Code</th>
                  <th>Product</th>
                  <th>Status</th>
                  <th>Inspections</th>
                  <th>Pass Rate</th>
                  <th>Failures</th>
                  <th>Started</th>
                  <th>Ended</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {batches.map((batch) => (
                  <tr key={batch.id}>
                    <td className="font-mono" style={{ fontWeight: 500 }}>{batch.batchCode}</td>
                    <td>{batch.productName || '—'}</td>
                    <td>
                      <span className={`badge ${
                        batch.status === 'running' ? 'badge-info' :
                        batch.status === 'completed' ? 'badge-pass' :
                        batch.status === 'quarantined' ? 'badge-fail' :
                        batch.status === 'released' ? 'badge-pass' : 'badge-neutral'
                      }`}>
                        {batch.status}
                      </span>
                    </td>
                    <td className="font-mono">{(batch.stats?.totalInspections || 0).toLocaleString()}</td>
                    <td className="font-mono" style={{
                      color: (batch.stats?.passRate || 0) >= 99 ? 'var(--status-pass)' :
                             (batch.stats?.passRate || 0) >= 95 ? 'var(--status-warning)' : 'var(--status-fail)'
                    }}>
                      {(batch.stats?.passRate || 0).toFixed(1)}%
                    </td>
                    <td className="font-mono" style={{ color: 'var(--status-fail)' }}>
                      {batch.stats?.failCount || 0}
                    </td>
                    <td className="text-sm text-muted">{new Date(batch.startedAt).toLocaleString()}</td>
                    <td className="text-sm text-muted">
                      {batch.endedAt ? new Date(batch.endedAt).toLocaleString() : '—'}
                    </td>
                    <td>
                      {batch.status === 'running' && (
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => handleComplete(batch.id)}
                        >
                          Complete
                        </button>
                      )}
                      {batch.status === 'completed' && (
                        <div className="flex gap-2">
                          <button
                            className="btn btn-sm btn-secondary"
                            onClick={() => handleStatusChange(batch.id, 'released')}
                          >
                            Release
                          </button>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleStatusChange(batch.id, 'quarantined')}
                          >
                            Quarantine
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );

  async function handleComplete(batchId: string) {
    try {
      await batchesApi.updateStatus(batchId, 'completed');
      setBatches((prev) => prev.map((b) => b.id === batchId ? { ...b, status: 'completed' } : b));
    } catch (err) {
      console.error('Failed to complete batch', err);
    }
  }

  async function handleStatusChange(batchId: string, status: string) {
    try {
      await batchesApi.updateStatus(batchId, status);
      setBatches((prev) => prev.map((b) => b.id === batchId ? { ...b, status } : b));
    } catch (err) {
      console.error('Failed to update batch status', err);
    }
  }
}
